import { ComponentType, ReactNode, useEffect, useState, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Filter, Search, X } from "lucide-react";
import { FilterSection, FilterField, FilterFieldButton } from "../Filters/common/FilterPanel.tsx";
import type { WorkOffersFilterState, WorkOffersFilterOption } from "../MercadoDeTrabajo/WorkOffersFilters.tsx";
import styles from "./WorkOfferFiltersPublic.module.scss";

const Presence = AnimatePresence as unknown as ComponentType<{ children: ReactNode; initial?: boolean }>;

interface WorkOfferFiltersPublicProps {
  skills: WorkOffersFilterOption[];
  zones: WorkOffersFilterOption[];
  value: WorkOffersFilterState;
  onChange: (next: WorkOffersFilterState) => void;
  totalCount: number;
  sidebar?: boolean;
}

const SEARCH_DEBOUNCE_MS = 250;

const WorkOfferFiltersPublic = ({ skills, zones, value, onChange, totalCount, sidebar = false }: WorkOfferFiltersPublicProps) => {
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState<"skills" | "zones" | null>(null);
  const [searchInput, setSearchInput] = useState(value.search);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const valueRef = useRef(value);
  const mobileRef = useRef<HTMLDivElement | null>(null);
  const desktopFiltersRef = useRef<HTMLDivElement | null>(null);
  valueRef.current = value;

  useEffect(() => {
    setSearchInput(value.search);
  }, [value.search]);

  const handleSearchChange = (next: string) => {
    setSearchInput(next);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      searchDebounceRef.current = null;
      onChange({ ...valueRef.current, search: next });
    }, SEARCH_DEBOUNCE_MS);
  };

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, []);

  const filterCount =
    (value.search.trim().length > 0 ? 1 : 0) +
    (value.skills.length > 0 ? 1 : 0) +
    (value.zones.length > 0 ? 1 : 0);

  const toggleSkill = (skill: string) => {
    const hasSkill = value.skills.includes(skill);
    const next = hasSkill ? value.skills.filter((item) => item !== skill) : [...value.skills, skill];
    onChange({ ...value, skills: next });
    setSelectedFilter(null);
    setMobileSection(null);
  };

  const toggleZone = (zone: string) => {
    const hasZone = value.zones.includes(zone);
    const next = hasZone ? value.zones.filter((item) => item !== zone) : [...value.zones, zone];
    onChange({ ...value, zones: next });
    setSelectedFilter(null);
    setMobileSection(null);
  };

  const resetFilters = () => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    setSearchInput("");
    onChange({ search: "", skills: [], zones: [] });
    setSelectedFilter(null);
    setMobileOpen(false);
    setMobileSection(null);
  };

  const removeSearch = () => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    setSearchInput("");
    onChange({ ...value, search: "" });
  };
  const removeSkill = (skill: string) => onChange({ ...value, skills: value.skills.filter((s) => s !== skill) });
  const removeZone = (zone: string) => onChange({ ...value, zones: value.zones.filter((z) => z !== zone) });

  const activeChips: { key: string; label: string; onRemove: () => void }[] = [];
  if (value.search.trim()) activeChips.push({ key: "search", label: `"${value.search.trim()}"`, onRemove: removeSearch });
  value.skills.forEach((s) => activeChips.push({ key: `skill-${s}`, label: s, onRemove: () => removeSkill(s) }));
  value.zones.forEach((z) => activeChips.push({ key: `zone-${z}`, label: z, onRemove: () => removeZone(z) }));

  const activeFiltersBlock = activeChips.length > 0 && (
    <div className={styles.activeChipsRow}>
      {activeChips.map(({ key, label, onRemove }) => (
        <span key={key} className={styles.activeChip}>
          {label}
          <button type="button" className={styles.activeChipRemove} onClick={onRemove} aria-label={`Quitar ${label}`}>
            <X size={12} />
          </button>
        </span>
      ))}
      {activeChips.length >= 1 && (
        <button type="button" className={styles.clearAllChip} onClick={resetFilters}>
          Limpiar todo
        </button>
      )}
    </div>
  );

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
      const handleClickOutside = (e: MouseEvent) => {
        if (mobileRef.current && !mobileRef.current.contains(e.target as Node)) {
          setMobileOpen(false);
          setMobileSection(null);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.body.style.overflow = "";
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
    return undefined;
  }, [mobileOpen]);

  useEffect(() => {
    if (!selectedFilter) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (desktopFiltersRef.current && !desktopFiltersRef.current.contains(e.target as Node)) {
        setSelectedFilter(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [selectedFilter]);

  const filterControls = (
    <>
      <FilterSection>
        <FilterField>
          <div className={styles.searchInputWrapper}>
            <Search className={styles.searchIcon} size={16} />
            <input
              className={styles.searchInput}
              type="text"
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Buscar por rubro, empresa, ubicación…"
            />
          </div>
        </FilterField>
      </FilterSection>
      <FilterSection>
        <FilterField>
          <FilterFieldButton
            title="Rubros"
            value={
              value.skills.length === 0
                ? undefined
                : value.skills.length === 1
                  ? value.skills[0]
                  : `${value.skills.length} seleccionados`
            }
            placeholder="Todos"
            onClick={() => setSelectedFilter((p) => (p === "skills" ? null : "skills"))}
            active={selectedFilter === "skills"}
          />
          {selectedFilter === "skills" && (
            <div className={styles.dropdownPanel}>
              {skills.length === 0 ? (
                <div className={styles.dropdownEmpty}>No hay rubros disponibles</div>
              ) : (
                <div className={styles.dropdownList}>
                  {skills.map(({ name, count }) => (
                    <button
                      key={name}
                      type="button"
                      className={styles.dropdownItem}
                      data-active={value.skills.includes(name)}
                      onClick={() => toggleSkill(name)}
                    >
                      {name} ({count})
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </FilterField>
      </FilterSection>
      <FilterSection>
        <FilterField>
          <FilterFieldButton
            title="Ubicación"
            value={
              value.zones.length === 0
                ? undefined
                : value.zones.length === 1
                  ? value.zones[0]
                  : `${value.zones.length} seleccionadas`
            }
            placeholder="Todas"
            onClick={() => setSelectedFilter((p) => (p === "zones" ? null : "zones"))}
            active={selectedFilter === "zones"}
          />
          {selectedFilter === "zones" && (
            <div className={styles.dropdownPanel}>
              {zones.length === 0 ? (
                <div className={styles.dropdownEmpty}>No hay ubicaciones disponibles</div>
              ) : (
                <div className={styles.dropdownList}>
                  {zones.map(({ name, count }) => (
                    <button
                      key={name}
                      type="button"
                      className={styles.dropdownItem}
                      data-active={value.zones.includes(name)}
                      onClick={() => toggleZone(name)}
                    >
                      {name} ({count})
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </FilterField>
      </FilterSection>
    </>
  );

  return (
    <div className={styles.root}>
      <div ref={desktopFiltersRef} className={`${styles.desktop} ${sidebar ? styles.sidebar : ""}`}>
        <div className={styles.compactRow}>
          <div className={styles.compactControls}>{filterControls}</div>
        </div>
        {activeFiltersBlock}
      </div>
      <div className={styles.mobile}>
        <div className={styles.mobileTriggerBar}>
          <button type="button" className={styles.mobileTrigger} onClick={() => setMobileOpen(true)}>
            <Filter size={18} />
            Filtros
            {filterCount > 0 && <span className={styles.mobileBadge}>{filterCount}</span>}
          </button>
          <div className={styles.mobileSummary}>
            <span className={styles.mobileSummaryCount}>{totalCount}</span>
            <span className={styles.mobileSummaryText}>{totalCount === 1 ? "oferta" : "ofertas"}</span>
          </div>
        </div>
        <Presence initial={false}>
          {mobileOpen && (
            <motion.div
              className={styles.mobileOverlay}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                ref={mobileRef}
                className={styles.mobileSheet}
                initial={{ y: "100%" }}
                animate={{ y: 0 }}
                exit={{ y: "100%" }}
                transition={{ type: "spring", damping: 26, stiffness: 280 }}
              >
                <header className={styles.mobileHeader}>
                  <div><h3>Filtros</h3></div>
                  <button
                    type="button"
                    className={styles.mobileClose}
                    onClick={() => {
                      setMobileOpen(false);
                      setMobileSection(null);
                    }}
                    aria-label="Cerrar filtros"
                  >
                    <X size={20} />
                  </button>
                </header>
                <div className={styles.mobileContent}>
                  {activeFiltersBlock}
                  <div className={styles.mobileSearch}>
                    <Search size={16} />
                    <input
                      type="text"
                      value={searchInput}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      placeholder="Buscar por rubro, empresa, ubicación…"
                    />
                  </div>
                  <FilterFieldButton
                    title="Rubros"
                    value={
                      value.skills.length === 0
                        ? "Todos"
                        : value.skills.length === 1
                          ? value.skills[0]
                          : `${value.skills.length} seleccionados`
                    }
                    onClick={() => setMobileSection((p) => (p === "skills" ? null : "skills"))}
                    active={mobileSection === "skills"}
                  />
                  <Presence initial={false}>
                    {mobileSection === "skills" && (
                      <motion.div
                        className={styles.mobileDropdown}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                      >
                        {skills.length === 0 ? (
                          <div className={styles.dropdownEmpty}>No hay rubros disponibles</div>
                        ) : (
                          <div className={styles.mobileChips}>
                            {skills.map(({ name, count }) => (
                              <button
                                key={name}
                                type="button"
                                className={styles.mobileChip}
                                data-active={value.skills.includes(name)}
                                onClick={() => toggleSkill(name)}
                              >
                                {name} ({count})
                              </button>
                            ))}
                          </div>
                        )}
                      </motion.div>
                    )}
                  </Presence>
                  <FilterFieldButton
                    title="Ubicación"
                    value={
                      value.zones.length === 0
                        ? "Todas"
                        : value.zones.length === 1
                          ? value.zones[0]
                          : `${value.zones.length} seleccionadas`
                    }
                    onClick={() => setMobileSection((p) => (p === "zones" ? null : "zones"))}
                    active={mobileSection === "zones"}
                  />
                  <Presence initial={false}>
                    {mobileSection === "zones" && (
                      <motion.div
                        className={styles.mobileDropdown}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                      >
                        {zones.length === 0 ? (
                          <div className={styles.dropdownEmpty}>No hay ubicaciones disponibles</div>
                        ) : (
                          <div className={styles.mobileChips}>
                            {zones.map(({ name, count }) => (
                              <button
                                key={name}
                                type="button"
                                className={styles.mobileChip}
                                data-active={value.zones.includes(name)}
                                onClick={() => toggleZone(name)}
                              >
                                {name} ({count})
                              </button>
                            ))}
                          </div>
                        )}
                      </motion.div>
                    )}
                  </Presence>
                </div>
              </motion.div>
            </motion.div>
          )}
        </Presence>
      </div>
    </div>
  );
};

export default WorkOfferFiltersPublic;
